/**
 * A video's length from its MP4 / QuickTime header (the `mvhd` box inside `moov`) — read in pieces, so a 300 MB
 * camera-roll .mov is never loaded whole. Works whatever the codec (HEVC included): the page can check the
 * three-minute rule even when the browser cannot play the file.
 */
export type ReadAt = (start: number, length: number) => Promise<Uint8Array>;

const u32 = (b: Uint8Array, o: number) => ((b[o]! << 24) >>> 0) + (b[o + 1]! << 16) + (b[o + 2]! << 8) + b[o + 3]!;
const u64 = (b: Uint8Array, o: number) => u32(b, o) * 2 ** 32 + u32(b, o + 4);
const typeAt = (b: Uint8Array, o: number) => String.fromCharCode(b[o]!, b[o + 1]!, b[o + 2]!, b[o + 3]!);

/** Walk the boxes in [start, end) and return where `type` begins (its header) and its size. */
async function find(read: ReadAt, start: number, end: number, type: string): Promise<{ at: number; header: number; size: number } | null> {
  let at = start;
  for (let guard = 0; at + 8 <= end && guard < 10_000; guard++) {
    const h = await read(at, Math.min(16, end - at));
    if (h.length < 8) return null;
    let size = u32(h, 0);
    let header = 8;
    if (size === 1) {
      if (h.length < 16) return null;
      size = u64(h, 8);
      header = 16;
    } else if (size === 0) size = end - at;
    if (size < header) return null;
    if (typeAt(h, 4) === type) return { at, header, size };
    at += size;
  }
  return null;
}

/** Seconds, or null when the file has no readable movie header. */
export async function mp4DurationSeconds(read: ReadAt, fileSize: number): Promise<number | null> {
  try {
    const moov = await find(read, 0, fileSize, 'moov');
    if (!moov) return null;
    const mvhd = await find(read, moov.at + moov.header, Math.min(fileSize, moov.at + moov.size), 'mvhd');
    if (!mvhd) return null;
    const b = await read(mvhd.at + mvhd.header, 32);
    const version = b[0];
    let timescale: number;
    let duration: number;
    if (version === 1) {
      timescale = u32(b, 20);
      duration = u64(b, 24);
    } else {
      timescale = u32(b, 12);
      duration = u32(b, 16);
    }
    if (!timescale) return null;
    return duration / timescale;
  } catch {
    return null;
  }
}

/** A minimal MP4 (ftyp + moov/mvhd + mdat) of a given length — for tests and the e2e "small MP4". */
export function tinyMp4(seconds: number, payload = 64): Uint8Array {
  const box = (type: string, body: Uint8Array) => {
    const out = new Uint8Array(8 + body.length);
    const n = out.length;
    out.set([(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255], 0);
    for (let i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
    out.set(body, 8);
    return out;
  };
  const be = (v: number) => [(v >>> 24) & 255, (v >>> 16) & 255, (v >>> 8) & 255, v & 255];
  const ftyp = box('ftyp', new Uint8Array([...'isom'].map((c) => c.charCodeAt(0)).concat([0, 0, 2, 0], [...'isomiso2mp41'].map((c) => c.charCodeAt(0)))));
  const mvhdBody = new Uint8Array(100);
  mvhdBody.set([0, 0, 0, 0], 0); // version 0, flags
  mvhdBody.set(be(1000), 12); // timescale
  mvhdBody.set(be(Math.round(seconds * 1000)), 16); // duration
  const moov = box('moov', box('mvhd', mvhdBody));
  const mdat = box('mdat', new Uint8Array(payload));
  const all = new Uint8Array(ftyp.length + moov.length + mdat.length);
  all.set(ftyp, 0);
  all.set(moov, ftyp.length);
  all.set(mdat, ftyp.length + moov.length);
  return all;
}
